import { useEffect, type ReactElement } from 'react';
import { Group, Loader, Text } from '@mantine/core';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import MegaGrid, { type MegaGridColumn } from '@/design/MegaGrid/MegaGrid';
import { useGlobalFilterSummary } from '@/features/global-filters/useGlobalFilterSummary';
import { formatCount, formatTokens, formatUsd, humanUser } from '@/shared/analytics/MetricFormatting';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { SurfaceDrillBreakdownViewModel, type DrillBreakdownRow } from './SurfaceDrillBreakdownViewModel';

const PAGE_SIZE = 25;

const COLUMNS: MegaGridColumn<DrillBreakdownRow>[] = [
    {
        key: 'person',
        header: 'Person',
        sortValue: (r) => humanUser(r.key),
        render: (r) => (
            <Text size="xs" fw={600}>
                {humanUser(r.key)}
            </Text>
        ),
    },
    {
        key: 'department',
        header: 'Department',
        sortValue: (r) => r.department,
        groupValue: (r) => r.department,
        render: (r) => <Text size="xs">{r.department}</Text>,
    },
    {
        key: 'turns',
        header: 'Prompts',
        align: 'right',
        sortValue: (r) => r.turns,
        render: (r) => (
            <Text size="xs" ff="monospace" fw={600}>
                {formatCount(r.turns)}
            </Text>
        ),
    },
    {
        key: 'sessions',
        header: 'Sessions',
        align: 'right',
        sortValue: (r) => r.sessions,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatCount(r.sessions)}
            </Text>
        ),
    },
    {
        key: 'total',
        header: 'Total tokens',
        align: 'right',
        sortValue: (r) => r.totalTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatTokens(r.totalTokens)}
            </Text>
        ),
    },
    {
        key: 'cost',
        header: 'Cost',
        align: 'right',
        sortValue: (r) => r.allocatedCostUsd,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatUsd(r.allocatedCostUsd)}
            </Text>
        ),
    },
    {
        key: 'in',
        header: 'Input tokens',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.inputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatTokens(r.inputTokens)}
            </Text>
        ),
    },
    {
        key: 'out',
        header: 'Output tokens',
        defaultHidden: true,
        align: 'right',
        sortValue: (r) => r.outputTokens,
        render: (r) => (
            <Text size="xs" ff="monospace" c="dimmed">
                {formatTokens(r.outputTokens)}
            </Text>
        ),
    },
];

interface PersonActivityGridProps {
    /** Server-side scope for the breakdown (slice filters + ambient); memoize at the call site. */
    filters: AnalyticsFilter[];
    onRowClick: (userId: string) => void;
    persistenceKey?: string;
}

/** Ranked people-behind-a-slice grid (person / department / prompts / sessions / tokens), shared by the generic drill page and the Use Cases Users tab. */
export default function PersonActivityGrid({ filters, onRowClick, persistenceKey }: PersonActivityGridProps): ReactElement {
    const vm = useViewModel(SurfaceDrillBreakdownViewModel);
    const externalFilters = useGlobalFilterSummary();

    useEffect(() => {
        void vm.load('user_id', filters);
    }, [vm, filters]);

    if (vm.errorMessage !== null) {
        return (
            <Text size="sm" c="red">
                {vm.errorMessage}
            </Text>
        );
    }
    if (vm.loading && vm.rows.length === 0) {
        return (
            <Group justify="center" gap="sm" py="xl">
                <Loader size="sm" />
                <Text size="sm" c="dimmed">
                    Ranking people…
                </Text>
            </Group>
        );
    }

    return (
        <MegaGrid
            columns={COLUMNS}
            rows={vm.rows}
            externalFilters={externalFilters}
            getRowKey={(r) => r.key}
            onRowClick={(r) => onRowClick(r.key)}
            searchAccessor={(r) => `${humanUser(r.key)} ${r.department}`}
            searchPlaceholder="Search people"
            persistenceKey={persistenceKey}
            striped
            density="comfortable"
            minWidth={560}
            maxHeight="calc(100vh - 320px)"
            clientPageSize={PAGE_SIZE}
        />
    );
}
